import AssignSpNotes from "@/components/features/AssignServiceProviderList/SpOverview/AssignSpNotes/AssignSpNotes";
import {
  CopyPaymentLinkButton,
  useCanCopyPaymentLink,
} from "@/components/features/AssignServiceProviderList/SpOverview/CopyPaymentLinkButton/CopyPaymentLinkButton";
import { CallAttemptHistory } from "@/components/features/AssignServiceProviderList/DispatchScript/CallAttemptHistory";
import { CallSpButton } from "@/components/features/AssignServiceProviderList/DispatchScript/CallSpButton";
import { DispatchDataFields } from "@/components/features/AssignServiceProviderList/DispatchScript/DispatchDataFields";
import { resolveDispatchBindings } from "@/components/features/AssignServiceProviderList/DispatchScript/resolveDispatchBindings";
import SpCallForm from "@/components/features/AssignServiceProviderList/SpOverview/SpCallForm/SpCallForm";
import SpScheduleCall from "@/components/features/AssignServiceProviderList/SpOverview/SpScheduleCall/SpScheduleCall";
import { SpW2Schedule } from "@/components/features/AssignServiceProviderList/SpOverview/SpW2Schedule/SpW2Schedule";
import {
  buildDirectionsUrl,
  buildSpAddressRows,
  formatWorkAddress,
} from "@/components/features/AssignServiceProviderList/spAddressRows";
import { Avatar, Card } from "@onengine/component-library";
import { Button } from "@/components/ui/Button/Button";
import Loader from "@/components/ui/Loader/Loader";
import { renderStatusBadge } from "@/components/ui/SpProfileCard/SpProfileCard";
import {
  isUuid,
  useAssignServiceProviderOverview,
  useDispatchScript,
} from "@/hooks/assignServiceProvider/useAssignServiceProviderQueries";
import { useGetProviderByTrades } from "@/hooks/googleFetch/serviceProviderByTrades/useGetProviderByTradesQueries";
import { useAssignServiceProviderStore } from "@/store/assignServiceProviderStore";
import { Anchor, Box, Flex, Stack, Text } from "@mantine/core";
import {
  IconAddressBook,
  IconAlertTriangle,
  IconCalendarTime,
  IconCash,
  IconCreditCard,
  IconLiveView,
  IconMapPin,
  // IconMessage,
  // IconPhone,
  IconSitemap,
  IconUserSearch,
} from "@tabler/icons-react";
import { createFileRoute, useRouterState } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import styles from "../assignServiceProviderLayout.module.scss";
import { getDistanceFromWorksite } from "@/lib/GoogleMaps/places";
import { useWorkVisitStore } from "@/store/workVisitStore";
import { NegotiatedTermsSection } from "@/components/features/AssignServiceProviderList/SpOverview/NegotiatedTermsSection/NegotiatedTermsSection";
import { SourcedOffPlatformSection } from "@/components/features/AssignServiceProviderList/SpOverview/SourcedOffPlatformSection/SourcedOffPlatformSection";
import { SpOverviewSection } from "@/components/features/WorkOrders/VisitDetail/SpOverviewSection/SpOverviewSection";

export const Route = createFileRoute(
  "/work-orders/$workOrderId/$visitId/assign-service-provider/_assignServiceProviderLayout/",
)({
  component: AssignServiceProviderOverview,
});

export type IAssignSpRouteState = {
  serviceProviderId?: string;
  showNonCompliantFormMode?: boolean;
  reactiveHire?: true;
  reactiveHireSource?: "google" | "database";
  activeTab?: string;
  scheduledDateTime?: string | null;
};

const PAYMENTTYPEMAP = {
  WIRE: "Wire Transfer",
  ACH: "ACH",
  CHECK: "Check",
  INVOICE: "Online invoice",
} as const;

type PaymentMethodKey = "WIRE" | "ACH" | "CHECK" | "INVOICE";

function AssignServiceProviderOverview() {
  const {
    showNonCompliantFormMode,
    serviceProviderId,
    reactiveHire,
    activeTab,
    reactiveHireSource,
    scheduledDateTime: scheduledDateTimeIso,
  } = useRouterState({
    select: (state) => state.location.state as IAssignSpRouteState,
  });

  const { visitId } = Route.useParams();
  const [showNonCompliantForm, setShowNonCompliantForm] = useState(false);
  const [showScheduleCallForm, setShowScheduleCallForm] = useState(false);
  const [isReactiveHireInitialLoading, setIsReactiveHireInitialLoading] = useState(false);
  const [showW2CalendarView, setShowW2CalendarView] = useState(false);
  const navigate = Route.useNavigate();
  const {
    orderedProviders,
    setResolvedProviderName,
    setWorksiteTimezone,
    listHasRequestProviderButton,
  } = useAssignServiceProviderStore();
  const scheduledDateTime = useMemo(
    () => (scheduledDateTimeIso ? new Date(scheduledDateTimeIso) : null),
    [scheduledDateTimeIso],
  );
  const [computedDistance, setComputedDistance] = useState<{
    providerId: string;
    distanceText: string;
    miles?: number | null;
    durationSeconds?: number | null;
  } | null>(null);
  const workVisitDetails = useWorkVisitStore((state) => state.workVisitDetails);

  const {
    data: overviewData,
    // isLoading,  // SERV-1936: Hide call and message options for SP
    isPending,
  } = useAssignServiceProviderOverview(
    serviceProviderId!,
    visitId,
    reactiveHire! && reactiveHireSource === "google",
  );

  const { data: reactiveHireData, isLoading: googleLoading } = useGetProviderByTrades();

  const { data: dispatchScript } = useDispatchScript(visitId);

  // Gates the wrapper below as well as the button itself. The dispatch-script branch wraps the
  // button in a padded, bordered section on its own, so hiding only the button would leave an
  // empty bordered strip for exactly the roles that lack the permission (SERV-2425).
  const canCopyPaymentLink = useCanCopyPaymentLink(serviceProviderId);

  useEffect(() => {
    if (!serviceProviderId) return;
    setResolvedProviderName(null);
    setComputedDistance(null);
    setShowW2CalendarView(false);
    setShowScheduleCallForm(false);
  }, [serviceProviderId]);

  // Create displayData based on reactiveHire flag
  const displayData = useMemo(() => {
    const addressParts = [
      overviewData?.address?.street,
      overviewData?.address?.city,
      overviewData?.address?.state,
      overviewData?.address?.zipCode,
    ].filter(Boolean);
    const overviewParsedAddress =
      addressParts.length > 0
        ? {
            addressInfo: addressParts.join(", "),
            latitude: overviewData?.address?.latitude,
            longitude: overviewData?.address?.longitude,
            locationInfo: overviewData?.address,
          }
        : undefined;
    const isReactiveHireFlow =
      reactiveHire &&
      serviceProviderId &&
      activeTab !== "accepted" &&
      reactiveHireSource == "google";

    if (isReactiveHireFlow) {
      // Reactive-hire data lives in `useGetProviderByTrades` which isn't
      // automatically refetched on a page reload, so a stale serviceProviderId
      // restored from router state has nothing to match against. Return null
      // here so the empty-state / exhausted-network screen renders instead of
      // an SP overview full of N/A fields.
      if (!reactiveHireData) {
        return null;
      }

      // Find the provider in reactiveHireData.reactive array
      const reactiveProvider = reactiveHireData.reactive?.find(
        (provider: any) => provider.id == serviceProviderId,
      );

      if (!reactiveProvider) {
        return null;
      }

      // Parse Google address to structured format
      const parsedAddress =
        reactiveProvider.fullAddress && reactiveProvider.location
          ? {
              addressInfo: reactiveProvider.fullAddress,
              latitude: reactiveProvider.location.lat,
              longitude: reactiveProvider.location.lng,
              locationInfo: {
                city: reactiveProvider.address?.city,
                state: reactiveProvider.address?.state,
                country: reactiveProvider.address?.country,
                zipCode: reactiveProvider.address?.zipcode,
              },
            }
          : undefined;
      // Map reactive provider data to match IAssignServiceProviderOverview structure
      return {
        fullName: reactiveProvider.fullName,
        status: "reactive_hire" as const,
        trades: [], // Google Places doesn't have trade info
        address: reactiveProvider.address,
        // SERV-2604: an off-platform provider found through Google has no
        // coverage or business record, so neither address exists. The single
        // address they were found with is carried on formattedAddress.
        dispatchAddress: null,
        businessAddress: null,
        isHourlyRate: null,
        rateType: null,
        hourlyRate: null,
        id: reactiveProvider.id,
        tripFee: null,
        hasIvc: null,
        ivcAmount: null,
        companyName: null,
        expectedAmount: null,
        flatRateAmount: null,
        nonHourlyMinimumJobCharge: null,
        nonHourlyassessmentCharge: null,
        chargesServiceAssessmentTripFee: null,
        averagePricePerJob: null,
        primaryPhone: "", // Not available from Google
        primaryEmail: "", // Not available from Google
        companions: [],
        spVisitStatus: undefined,
        scheduledTime: null,
        // Additional fields from Google
        rating: reactiveProvider.rating,
        totalRatings: reactiveProvider.totalRatings,
        isOpenNow: reactiveProvider.isOpenNow,
        formattedAddress: reactiveProvider.fullAddress,
        parsedAddress: parsedAddress, // Structured address for form
        visitProviderId: reactiveProvider.visitProviderId,
        paymentMethod: reactiveProvider.paymentMethod,
        distanceFromWorkSite: reactiveProvider.distance,
        companyNotes: reactiveProvider.companyNotes,
        accoreeNotes: reactiveProvider.accoreeNotes,
        canDeductOrCreditAssessmentCost: null,
        providerType: null,
        profilePhotoUrl: null,
        rejection: null,
      };
    }

    if (!overviewData) return null;
    return { ...overviewData, overviewParsedAddress };
  }, [
    reactiveHire,
    serviceProviderId,
    reactiveHireData,
    overviewData,
    activeTab,
    reactiveHireSource,
  ]);

  useEffect(() => {
    if (displayData?.fullName) {
      setResolvedProviderName(displayData.fullName);
    }
  }, [displayData?.fullName]);

  useEffect(() => {
    const tz = (displayData?.address as { timezone?: string | null })?.timezone ?? null;
    setWorksiteTimezone(tz);
  }, [displayData?.address, setWorksiteTimezone]);

  // Handle initial loading for reactive hire
  useEffect(() => {
    if (reactiveHire && serviceProviderId && !reactiveHireData) {
      setIsReactiveHireInitialLoading(true);

      // Set a timeout to show loading for at least 2 seconds
      const timer = setTimeout(() => {
        setIsReactiveHireInitialLoading(false);
      }, 2000);

      return () => clearTimeout(timer);
    } else {
      setIsReactiveHireInitialLoading(false);
    }
  }, [reactiveHire, serviceProviderId, reactiveHireData]);

  // // Sync showNonCompliantForm with showNonCompliantFormMode and spStatus
  useEffect(() => {
    if (displayData) {
      const showFormMode =
        displayData.status === "reactive_hire" || displayData.status === "non-compliant";
      if (showNonCompliantFormMode && showFormMode) {
        setShowNonCompliantForm(true);
      } else {
        setShowNonCompliantForm(false);
      }
    }
  }, [showNonCompliantFormMode, displayData]);

  const finalDistanceFromWorksite = useMemo(() => {
    if (!displayData) return null;

    // Google already has distance
    if (reactiveHireSource === "google") {
      return displayData.distanceFromWorkSite ?? null;
    }

    // DB provider – use computed distance
    return computedDistance?.distanceText;
  }, [displayData, reactiveHireSource, computedDistance]);

  useEffect(() => {
    if (!displayData) return;
    if (reactiveHireSource === "google") return;
    if (!workVisitDetails) return;
    if (computedDistance) return;

    const lat = (displayData.address as any)?.latitude;
    const lng = (displayData.address as any)?.longitude;

    if (!lat || !lng) return;

    getDistanceFromWorksite(
      Number(workVisitDetails?.address?.latitude),
      Number(workVisitDetails?.address?.longitude),
      Number(lat),
      Number(lng),
    ).then((distance) => {
      if (distance?.text) {
        setComputedDistance({
          providerId: serviceProviderId!,
          distanceText: distance.text,
          miles: distance.miles,
          durationSeconds: distance.durationSeconds,
        });
      }
    });
  }, [displayData, serviceProviderId, reactiveHireSource, computedDistance]);

  // Show loading for reactive hire or regular data
  // if (reactiveHire ? googleLoading || isReactiveHireInitialLoading : isLoading) {
  //   return (
  //     <Card className={styles.spOverviewCard}>
  //       <div
  //         style={{
  //           width: "100%",
  //           height: "50vh",
  //           display: "flex",
  //           alignItems: "center",
  //           justifyContent: "center",
  //         }}
  //       >
  //         <Loader />
  //       </div>
  //     </Card>
  //   );
  // }
  const shouldShowLoading = useMemo(() => {
    // For Google reactive hire, check Google loading
    if (reactiveHire && reactiveHireSource === "google") {
      return googleLoading || isReactiveHireInitialLoading;
    }

    // For database reactive hire or regular providers, check overview API loading.
    // isPending stays true during the brief moment before the fetch starts (isLoading would be false).
    return isPending;
  }, [reactiveHire, reactiveHireSource, googleLoading, isReactiveHireInitialLoading, isPending]);

  const allSuggestedAssigned =
    activeTab !== "accepted" &&
    !serviceProviderId &&
    orderedProviders.length > 0 &&
    orderedProviders.every((sp: any) => sp.visitStatus !== null);
  // SERV-2715: exactly one request-provider button on screen, the list's or the card's.
  const showRequestButton = !listHasRequestProviderButton;

  if (allSuggestedAssigned) {
    return <NetworkCoverageExhausted showRequestButton={showRequestButton} />;
  }

  if (!serviceProviderId) {
    return <NetworkCoverageExhausted showRequestButton={showRequestButton} />;
  }

  // Show loading screen
  if (shouldShowLoading) {
    return (
      <Card className={styles.spOverviewCard}>
        <div
          style={{
            width: "100%",
            height: "50vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Loader />
        </div>
      </Card>
    );
  }

  if (!displayData) return <NetworkCoverageExhausted showRequestButton={showRequestButton} />;

  const spStatus = displayData.status;
  const isW2Provider = spStatus === "w2";
  const showFormMode = spStatus == "reactive_hire" || spStatus == "non-compliant";
  const visitProviderId = displayData?.visitProviderId;

  const hasDispatchScript = Boolean(dispatchScript);
  const dispatchBindings = resolveDispatchBindings(displayData as any, {
    miles: computedDistance?.miles,
    durationSeconds: computedDistance?.durationSeconds,
    text: finalDistanceFromWorksite,
  });

  const paymentMethod = displayData.paymentMethod as PaymentMethodKey | undefined;
  const paymentInfo: { label: string; value: string | null }[] = [
    {
      label: "Payment Type",
      value: paymentMethod ? PAYMENTTYPEMAP[paymentMethod] : null,
    },
  ];

  const isHourlyRate = displayData.isHourlyRate !== false;
  const ratesInfo: { label: string; value: string | null }[] = [
    {
      label: "SP Rate type",
      value: displayData.rateType ?? null,
    },
    ...(isHourlyRate
      ? [
          {
            label: "Hourly Rate",
            value: displayData.hourlyRate != null ? `$ ${displayData.hourlyRate}` : null,
          },
          {
            // 11.R: this row was labelled IVC but read tripFee. The IVC is its
            // own charge and the backend now returns it; trip fee keeps its own
            // row so the value is not lost.
            label: "IVC",
            value: displayData.ivcAmount != null ? `$ ${displayData.ivcAmount}` : null,
          },
          {
            label: "Trip Fee",
            value: displayData.tripFee != null ? `$ ${displayData.tripFee}` : null,
          },
        ]
      : [
          {
            label: "Expected Amount",
            value:
              (displayData.flatRateAmount ?? displayData.expectedAmount) != null
                ? `$ ${displayData.flatRateAmount ?? displayData.expectedAmount}`
                : null,
          },
          {
            label: "Assessment Charge",
            value:
              displayData.nonHourlyassessmentCharge != null
                ? `$ ${displayData.nonHourlyassessmentCharge}`
                : null,
          },
          {
            label: "Minimum Job Charge",
            value:
              displayData.nonHourlyMinimumJobCharge != null
                ? `$ ${displayData.nonHourlyMinimumJobCharge}`
                : null,
          },
          {
            // SERV-2946: the IVC is not tied to the rate model. A fixed-rate SP
            // can carry one, the backend sends it on both arms, and this arm
            // used to drop it on the floor.
            label: "IVC",
            value: displayData.ivcAmount != null ? `$ ${displayData.ivcAmount}` : null,
          },
          {
            label: "Trip Fee",
            value: displayData.tripFee != null ? `$ ${displayData.tripFee}` : null,
          },
        ]),
    ...(displayData.chargesServiceAssessmentTripFee != null
      ? [
          {
            label: "Charges Assessment Trip Fee",
            value: displayData.chargesServiceAssessmentTripFee ? "Yes" : "No",
          },
        ]
      : []),
    ...(displayData.canDeductOrCreditAssessmentCost != null
      ? [
          {
            label: "Deducts / Credits Assessment Cost",
            value: displayData.canDeductOrCreditAssessmentCost ? "Yes" : "No",
          },
        ]
      : []),
    {
      label: "Avg. Price Per Job",
      value: displayData.averagePricePerJob != null ? `$ ${displayData.averagePricePerJob}` : null,
    },
  ];

  // SERV-2604: both addresses always render, even when empty, because a missing
  // address is itself something the dispatcher needs to see rather than a row
  // that silently disappears. SpOverviewSection shows "N/A" for a null value.
  // The rationale for the two rows and for how sameness is decided lives with
  // buildSpAddressRows.
  const addressRows = buildSpAddressRows(displayData, workVisitDetails?.address);

  // The address stays plain text so it can still be selected and copied, which
  // is what a dispatcher does with it; the route is a separate affordance.
  const addressValue = (row: { text: string | null; directionsUrl: string | null }) => {
    if (!row.text) return null;
    if (!row.directionsUrl) return row.text;
    return (
      <Flex gap={"var(--spacing-x-small, 8px)"} align={"baseline"} wrap={"wrap"}>
        <span>{row.text}</span>
        <Anchor
          href={row.directionsUrl}
          target="_blank"
          rel="noopener noreferrer"
          className={styles.spDirectionsLink}
        >
          Directions
        </Anchor>
      </Flex>
    );
  };

  const contactInfo: { label: string; value: string | React.ReactNode }[] = [
    // 11.R: companies show their legal business name. It is null for an
    // individual, so the row drops out rather than repeating the person's name.
    ...(displayData.companyName ? [{ label: "Company Name", value: displayData.companyName }] : []),
    {
      label: "Primary Phone Number",
      value: displayData?.primaryPhone ?? null,
    },
    {
      label: "Email Address",
      value: displayData?.primaryEmail ?? null,
    },
    {
      label: "Dispatch Address",
      // An off-platform provider sourced from Google has no coverage record, so
      // the address they were found with is the only one there is.
      value:
        addressValue(addressRows.dispatch) ??
        addressValue({
          text: displayData?.formattedAddress ?? null,
          directionsUrl: buildDirectionsUrl(
            displayData?.formattedAddress,
            formatWorkAddress(workVisitDetails?.address),
          ),
        }),
    },
    {
      label: "Company Address",
      value: addressValue(addressRows.company),
    },
  ];

  const companionInfo: { label: string; value: string | null }[] = (
    displayData.companions ?? []
  ).map((companion, index) => ({
    label: `Companion ${index + 1}`,
    value: companion.fullName ? companion.fullName : (companion.companionRelation ?? null),
  }));

  const noteSections = [
    {
      title: "Company Notes",
      ctaText: "New Company Note",
      noteType: "company",
      notesData: displayData.companyNotes || [],
    },
    {
      title: "Accoree Notes",
      ctaText: "New Accoree Note",
      noteType: "accoree",
      notesData: displayData.accoreeNotes || [],
    },
  ];

  const handleShowForm = (date: Date | null) => {
    navigate({
      state: (prev: any) => ({
        ...prev,
        showNonCompliantFormMode: true,
        scheduledDateTime: date ? date.toISOString() : null,
      }),
      replace: true,
    });
    setShowNonCompliantForm(true);
  };

  if (googleLoading) {
    return (
      <Card className={styles.spOverviewCard}>
        <div
          style={{
            width: "100%",
            height: "50vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Loader />
        </div>
      </Card>
    );
  }

  const getGoogleMapsLink = (latitude: string, longitude: string) => {
    return `https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`;
  };

  const w2ScheduleProps = {
    spVisitStatus: displayData?.spVisitStatus?.toLowerCase(),
    scheduledTime: displayData.scheduledTime ?? null,
    serviceProviderId: serviceProviderId,
    spName: displayData.fullName ?? "",
    visitProviderId,
    timezone: (displayData.address as any)?.timezone ?? null,
    onCalendarOpen: () => setShowW2CalendarView(true),
    onCalendarClose: () => setShowW2CalendarView(false),
  };

  const renderScheduleSection = () => {
    if (showNonCompliantForm) {
      return (
        <Stack style={{ width: "100%" }}>
          {hasDispatchScript && dispatchScript ? (
            <DispatchDataFields
              schema={dispatchScript.schema}
              bindings={dispatchBindings}
              sectionClassName={styles.spOverviewSection}
            />
          ) : null}
          <SpCallForm
            initialAddress={
              reactiveHireSource === "google"
                ? (displayData as any).parsedAddress
                : ((displayData as any).overviewParsedAddress ?? undefined)
            }
            spName={displayData.fullName}
            scheduledDateTime={scheduledDateTime}
            primaryPhone={displayData.primaryPhone}
            primaryEmail={displayData.primaryEmail}
            paymentMethod={displayData.paymentMethod}
            // No type predicate: asserting a narrower shape here threw away the
            // companion fields SpCallForm seeds its drafts from, so dropping one
            // upstream would still typecheck and the panel would silently write a
            // default over the provider's answer.
            companions={(displayData.companions ?? []).filter((c) => Boolean(c?.id))}
            companyNoteId={(displayData.companyNotes as any)?.[0]?.id}
            companyNoteText={(displayData.companyNotes as any)?.[0]?.noteText}
            spType={spStatus ?? ""}
            spId={serviceProviderId}
            dispatchScript={dispatchScript ?? null}
          />
        </Stack>
      );
    }

    // Keep one tree shape for both states: moving SpW2Schedule between branches
    // remounts it and wipes the schedule the user just opened for editing.
    const isW2CalendarOpen = isW2Provider && showW2CalendarView;

    // SERV-3036: the dispatcher's order (Tony, 9/28 walkthrough). Everything checked before the
    // call, how to reach the provider and what they charge, sits above Assign; companions follow.
    return (
      <>
        {!isW2CalendarOpen &&
          (hasDispatchScript && dispatchScript ? (
            <>
              <DispatchDataFields
                schema={dispatchScript.schema}
                bindings={dispatchBindings}
                sectionClassName={styles.spOverviewSection}
              />
              {showFormMode && canCopyPaymentLink && (
                <Box className={styles.spOverviewSection}>
                  <CopyPaymentLinkButton serviceProviderId={serviceProviderId} />
                </Box>
              )}
            </>
          ) : (
            <>
              {showFormMode && (
                <Box className={styles.spOverviewSection}>
                  <Flex align={"center"} gap={"var(--spacing-medium, 16px)"} w={"100%"}>
                    <IconMapPin size={16} />
                    {displayData.address ? (
                      <Flex align={"center"} w={"100%"} justify={"space-between"}>
                        <Anchor
                          href={getGoogleMapsLink(
                            (displayData.address as any).latitude,
                            (displayData.address as any).longitude,
                          )}
                          target="_blank"
                          className={styles.reactiveHireLocation}
                        >
                          {displayData.formattedAddress ?? displayData.address.street}
                        </Anchor>
                        <a
                          target="_blank"
                          href={getGoogleMapsLink(
                            (displayData.address as any).latitude,
                            (displayData.address as any).longitude,
                          )}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            height: 22,
                            borderRadius: "var(--radius-rounded-lg, 120px)",
                            border: "1px solid var(--border-default, #CFD4DD)",
                            background: "var(--bg-neutral-strong, #CFD4DD)",
                            cursor: "pointer",
                            textDecoration: "none",
                            color: "var(--text-strong, #131927)",
                            padding: "var(--spacing-0, 0) var(--spacing-2x-small, 6px)",
                            gap: "var(--spacing-4x-small, 2px)",
                          }}
                          rel="noreferrer"
                        >
                          <IconLiveView size={16} />{" "}
                          {finalDistanceFromWorksite && (
                            <span className={styles.reactiveHireDistance}>
                              {finalDistanceFromWorksite} away from Worksite
                            </span>
                          )}
                        </a>
                      </Flex>
                    ) : (
                      <Text
                        style={{
                          fontSize: "var(--text-font-size-medium, 14px)",
                          color: "var(--text-placeholder, #9DA3AE)",
                        }}
                      >
                        N/A
                      </Text>
                    )}
                  </Flex>
                </Box>
              )}
              {contactInfo && contactInfo.length > 0 && (
                <Box className={styles.spOverviewSection}>
                  <SpOverviewSection
                    overViewData={contactInfo}
                    icon={<IconAddressBook size={20} />}
                  />
                  {/* SERV-1936: Hide call and message options for SP - Start */}
                  {/* <Flex gap={"var(--spacing-small, 12px)"} align="center">
                <Button
                  type="button"
                  className={styles.scheduleCallButton}
                  variant="secondary"
                  onClick={() => {}}
                  disabled={isLoading}
                  loading={isLoading}
                >
                  <Flex align="center" gap="2px">
                    <IconPhone size={16} />
                    <Text className={styles.schedulebuttonText}>Call</Text>
                  </Flex>
                </Button>
                <Button
                  variant="tertiary"
                  style={{
                    border: " 1px solid var(--border-default, #CFD4DD)",
                    borderRadius: "var(--radius-base, 8px)",
                    height: "36px",
                    width: "40px",
                  }}
                >
                  <IconMessage size={16} />
                </Button>
              </Flex>
            */}
                  {/* SERV-1936: Hide call and message options for SP - End */}
                </Box>
              )}
              {/* Above the standing rates: for an off-platform provider those
                  are empty and this is the only rate information there is. */}
              <Box className={styles.spOverviewSection}>
                <NegotiatedTermsSection
                  visitId={visitId}
                  serviceProviderId={serviceProviderId}
                  isW2Provider={isW2Provider}
                />
              </Box>
              <Box className={styles.spOverviewSection}>
                <SourcedOffPlatformSection
                  visitId={visitId}
                  serviceProviderId={serviceProviderId}
                />
              </Box>
              {ratesInfo && ratesInfo.length > 0 && (
                <Box className={styles.spOverviewSection}>
                  <SpOverviewSection overViewData={ratesInfo} icon={<IconCash size={20} />} />
                </Box>
              )}
              {showFormMode && paymentInfo && (
                <Box className={styles.spOverviewSection}>
                  <Stack gap="var(--spacing-small, 12px)" style={{ width: "100%" }}>
                    <SpOverviewSection
                      overViewData={paymentInfo}
                      icon={<IconCreditCard size={20} />}
                    />
                    <CopyPaymentLinkButton serviceProviderId={serviceProviderId} />
                  </Stack>
                </Box>
              )}
            </>
          ))}
        {isW2Provider ? (
          <SpW2Schedule {...w2ScheduleProps} />
        ) : (
          <Box className={styles.spOverviewSection}>
            {!displayData.scheduledTime && !showScheduleCallForm ? (
              <Stack style={{ width: "100%" }} gap="var(--spacing-medium, 16px)">
                <Flex gap="var(--spacing-small, 12px)" align="flex-start">
                  <IconCalendarTime size={20} style={{ marginTop: 2, flexShrink: 0 }} />
                  <Stack gap="var(--spacing-4x-small, 4px)">
                    <Text
                      style={{
                        fontSize: "var(--text-font-size-medium, 14px)",
                        fontWeight: 500,
                        color: "var(--text-strong, #131927)",
                      }}
                    >
                      Scheduled Onsite Date and Time
                    </Text>
                    <Text
                      style={{
                        fontSize: "var(--text-font-size-medium, 14px)",
                        color: "var(--text-placeholder, #9DA3AE)",
                      }}
                    >
                      Not scheduled yet
                    </Text>
                  </Stack>
                </Flex>
                <Button
                  style={{
                    width: "100%",
                    background: "var(--bg-neutral-bold-default, #363d4d)",
                    color: "var(--text-inverse-default, #fff)",
                    border: "none",
                    borderRadius: "var(--radius-base, 8px)",
                  }}
                  onClick={() => {
                    if (showFormMode) {
                      handleShowForm(null);
                    } else {
                      setShowScheduleCallForm(true);
                    }
                  }}
                >
                  {/* SERV-1938: Update button text */}
                  Assign Service Provider
                </Button>
              </Stack>
            ) : (
              <SpScheduleCall
                spVisitStatus={
                  displayData?.spVisitStatus?.toLowerCase() as
                    | "accepted"
                    | "rejected"
                    | "no-answer"
                    | "on hold"
                    | "on_hold"
                }
                scheduledTime={displayData.scheduledTime ?? null}
                setShowNonCompliantForm={handleShowForm}
                spStatus={spStatus}
                visitProviderId={visitProviderId}
                rejection={displayData.rejection}
                dispatchScript={dispatchScript ?? null}
              />
            )}
          </Box>
        )}
        {!isW2CalendarOpen && !hasDispatchScript && (
          <Box className={styles.spOverviewSection}>
            {!displayData.companions?.length ? (
              <Flex gap="var(--spacing-small, 12px)" align="center">
                <IconSitemap size={20} style={{ flexShrink: 0 }} />
                <Text
                  style={{
                    fontSize: "var(--text-font-size-medium, 14px)",
                    color: "var(--text-placeholder, #9DA3AE)",
                  }}
                >
                  No companions available
                </Text>
              </Flex>
            ) : (
              <SpOverviewSection
                overViewData={companionInfo}
                icon={<IconSitemap size={20} />}
                noDataPlaceholder="No companions available"
              />
            )}
          </Box>
        )}
        {!isW2CalendarOpen && (
          <Box className={styles.spOverviewSection}>
            <AssignSpNotes
              noteSections={noteSections}
              serviceProviderId={serviceProviderId ?? ""}
              visitId={visitId}
            />
          </Box>
        )}
      </>
    );
  };

  const trades = displayData?.trades;

  return (
    <Stack style={{ width: "100%", overflow: "auto" }} className={styles.hideScroll}>
      <Card className={`${styles.spOverviewCard} ${styles.hideScroll}`}>
        <Stack style={{ width: "100%", gap: 0 }}>
          <Flex align={"center"} justify={"space-between"} className={styles.spOverviewHeader}>
            <Flex gap={"var(--spacing-medium, 16px)"} align={"center"}>
              <div>
                <Avatar
                  src={displayData.profilePhotoUrl ?? undefined}
                  name={displayData.fullName}
                  type="photo"
                  size="xl"
                  className={styles.avatar}
                />
              </div>

              <Stack gap={" var(--spacing-xsmall, 4px)"} align="flex-start" style={{ flex: 1 }}>
                <Flex gap={"var(--spacing-small, 8px)"} align={"center"}>
                  <Text className={styles.spProfileName}>{displayData.fullName}</Text>{" "}
                  <div>{renderStatusBadge(displayData.status)}</div>
                </Flex>
                {trades && trades.length > 0 && (
                  <div className={styles.spProfileTrade}>
                    {trades.map((trade, index) => {
                      const isLast = index === trades.length - 1;
                      return (
                        <Text key={trade.id} className={styles.spTradesText}>
                          {trade.name}
                          {!isLast && ","}
                        </Text>
                      );
                    })}
                  </div>
                )}
              </Stack>
            </Flex>
            <Flex align={"center"} gap={"var(--spacing-medium, 16px)"}>
              {hasDispatchScript && !isW2Provider && (
                <CallSpButton phone={displayData.primaryPhone} />
              )}
              {!showFormMode && displayData.address && (
                <Flex align={"center"} gap={"var(--spacing-medium, 16px)"}>
                  <Flex className={styles.spLocation}>
                    <IconMapPin size={16} />
                    <Text className={styles.spLocationText}>
                      {reactiveHire
                        ? (displayData as any).formattedAddress
                        : displayData.address.city}
                    </Text>
                  </Flex>
                  <a
                    target="_blank"
                    href={getGoogleMapsLink(
                      (displayData.address as any).latitude,
                      (displayData.address as any).longitude,
                    )}
                    className={styles.spLocationLink}
                    rel="noreferrer"
                  >
                    <IconLiveView size={16} />
                    {/* A dispatch script places distance through its own bindings, so the
                        header stays icon-only there to avoid showing it twice. */}
                    {!hasDispatchScript && finalDistanceFromWorksite && (
                      <span className={styles.reactiveHireDistance}>
                        {finalDistanceFromWorksite} away from Worksite
                      </span>
                    )}
                  </a>
                </Flex>
              )}
            </Flex>
          </Flex>

          <Stack gap={0} style={{ width: "100%" }}>
            {renderScheduleSection()}
            {isUuid(serviceProviderId) && !isW2Provider && (
              <CallAttemptHistory
                visitId={visitId}
                spId={serviceProviderId}
                schema={dispatchScript?.schema ?? null}
                sectionClassName={styles.spOverviewSection}
              />
            )}
          </Stack>
        </Stack>
      </Card>
      {/* 11.R: "Next Service Provider" removed. It stayed disabled until the
          current provider had a visit status, which is the forced-traversal rule
          the redesign dropped, and every provider in the list is now directly
          clickable so advancing needs no dedicated control. */}
    </Stack>
  );
}

interface NetworkCoverageExhaustedProps {
  // SERV-2715: true only while the provider list's footer button is off screen;
  // otherwise the copy points at that button instead of repeating it.
  showRequestButton: boolean;
}

// Exported so the empty state can be tested without mounting the route.
export function NetworkCoverageExhausted({ showRequestButton }: NetworkCoverageExhaustedProps) {
  const navigate = Route.useNavigate();

  const openRequestProvider = () =>
    navigate({
      to: "/work-orders/$workOrderId/$visitId/assign-service-provider/request-provider",
    });

  return (
    <Card className={styles.spOverviewCard}>
      <div
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 20,
        }}
      >
        <Stack align="center" justify="center" gap={8}>
          <Box
            style={{
              borderRadius: "50%",
              width: 40,
              height: 40,
              background: "var(--bg-warning-inverse-default, #FEE3B0)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <IconAlertTriangle size={24} color="var(--icon-warning-strong, #65330F)" />
          </Box>
          <Text
            style={{
              color: "var(--text-strong, #131927)",
              fontSize: "var(--heading-font-size-h5, 16px)",
              fontWeight: 700,
            }}
          >
            Network Coverage Exhausted
          </Text>
          <Text style={{ fontSize: "var(--text-font-size-medium, 14px)" }}>
            <span style={{ color: "var(--text-weak, #646D7E)", fontWeight: 400 }}>
              All matching internal and contracted providers have been contacted or are unavailable.
              You can submit a
            </span>{" "}
            <span style={{ color: "var(--text-weak, #646D7E)", fontWeight: 400 }}>&quot;</span>
            <span style={{ color: "var(--text-default, #363D4D)", fontWeight: 600 }}>
              Request to Add New Service Provider
            </span>
            <span style={{ color: "var(--text-weak, #646D7E)", fontWeight: 400 }}>
              &quot;
              {showRequestButton ? "" : " from the button below the provider list"} to bring an open
              market provider into the system.
            </span>
          </Text>
          {showRequestButton && (
            <Button
              className={styles.assignReactiveHireButton}
              style={{ marginTop: "var(--spacing-x-large, 24px)" }}
              onClick={openRequestProvider}
            >
              <IconUserSearch size={16} style={{ marginRight: 4 }} />
              Request to Add New Service Provider
            </Button>
          )}
        </Stack>
      </div>
    </Card>
  );
}
